import { lazy, Suspense, useEffect, useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import useNavigationMenus from 'common/hooks/useMenus';
import ErrorPage, { ErrorType } from 'common/components/errorPages/ErrorPage';
import { sanatizeNavigationUrl, useCompareUrlWithProjectId } from 'common/hooks/route';
import FullLayout from '../../Layouts/FullLayout';
import { Spinner } from 'common/components/loader/Spinner';
import {
  ALIGNMENT_ROUTES,
  ANALYZE_ROUTES,
  APP_ROOT,
  CAPTURE_ROUTES,
  COACH_ROUTES,
  DEVELOPMENT_ROUTES,
  INSIGHTS_MODULE_ROUTES,
  INSIGHTS_ROUTES,
  MY_IMPACT_MODULE_ROUTES,
  PROFILE_CAPTURE_ROUTES,
  REPORTS_ROUTES,
} from '../../routes/routes.const';
import { PROJECT_ID_PLACEHOLDER } from 'common/constants';
import { useModuleProjectContext } from 'common/contexts/useModuleProjectContext';
import { SelectionContextProvider } from 'common/contexts/useSelectionContext';
import { resetEditor, selectId } from 'common/store/slices/dashboard-editor.slice';
import { useDispatch, useSelector } from 'react-redux';
import { CaptureProvider } from 'common/contexts/useCaptureContext';

const DevelopmentPage = lazy(() => import('modules/insights/pages/development/developmentPage'));
const ComponentLibrary = lazy(() => import('modules/insights/pages/ComponentLibrary'));
const AlignmentComparePage = lazy(
  () => import('modules/insights/pages/analyze/alignment/AlignmentComparePage')
);
const AlignmentDetailPage = lazy(
  () => import('modules/insights/pages/analyze/alignment/AlignmentDetailPage')
);
const AlignmentSummaryPage = lazy(
  () => import('modules/insights/pages/analyze/alignment/AlignmentSummaryPage')
);
const InsightsComparePage = lazy(
  () => import('modules/insights/pages/analyze/insights/InsightsComparePage')
);
const InsightsDetailPage = lazy(
  () => import('modules/insights/pages/analyze/insights/InsightsDetailPage')
);
const InsightsSummaryPage = lazy(
  () => import('modules/insights/pages/analyze/insights/InsightsSummaryPage')
);
const InteractionPage = lazy(
  () => import('modules/insights/pages/analyze/interaction/Interaction')
);
const ThemeAnalyticsPage = lazy(
  () => import('modules/insights/pages/analyze/theme-analytics/ThemeAnalyticsPage')
);
const CoachSummaryPage = lazy(() => import('modules/insights/pages/coach/CoachSummaryPage'));
const CoachDetailPage = lazy(() => import('modules/insights/pages/coach/CoachDetailPage'));
const ProfileCapturePage = lazy(() => import('modules/insights/pages/capture/ProfileCapturePage'));
const CapturePage = lazy(() => import('modules/insights/pages/capture/InsightsCaptureLanding'));
const CaptureNewActivityPage = lazy(
  () => import('modules/insights/pages/capture/InsightsCaptureNewActivity')
);
const CaptureNewInsightPage = lazy(
  () => import('modules/insights/pages/capture/InsightsCaptureNewInsight')
);
const DashboardEditorPage = lazy(
  () => import('modules/insights/pages/reports/DashboardEditorPage')
);
const DashboardLibraryPage = lazy(
  () => import('modules/insights/pages/reports/DashboardLibraryPage')
);
const DashboardViewPage = lazy(() => import('modules/insights/pages/reports/DashboardViewPage'));
const DashboardWizardPage = lazy(
  () => import('modules/insights/pages/reports/DashboardWizardPage')
);
const WidgetCatalogPage = lazy(() => import('modules/insights/pages/reports/WidgetCatalogPage'));
const WidgetConfiguratorPage = lazy(
  () => import('modules/insights/pages/reports/WidgetConfiguratorPage')
);
const ElsieAiPage = lazy(
  () => import('modules/insights/pages/analyze/elsieai/InsightsElsieAIPage')
);
const ElsieAiPageV2 = lazy(
  () => import('modules/insights/pages/analyze/elsieai-v2/InsightsElsieAIPageV2')
);
const MirPage = lazy(() => import('modules/insights/pages/analyze/mir/MirPage'));

const MyImpactInteractionKpisPage = lazy(
  () => import('modules/user/pages/MyImpactInteractionKpis')
);
const MyImpactCollectedResponsesPage = lazy(
  () => import('modules/user/pages/MyImpactCollectedResponses')
);

const CoachTeamDetailInteractionKpisPage = lazy(
  () => import('modules/insights/pages/coach/CoachTeamDetailInteractionKpisPage')
);
const CoachTeamDetailCollectedResponsesPage = lazy(
  () => import('modules/insights/pages/coach/CoachTeamDetailCollectedResponsesPage')
);
const SurveyConfiguratorPage = lazy(
  () => import('modules/planning/pages/SurveyConfigurator/SurveyConfiguratorPage')
);

function DynamicModule() {
  const dispatch = useDispatch();
  const { compareUrl } = useCompareUrlWithProjectId();
  const { module, projectId, page, subPage, tab } = useParams();
  const { tabMenuItems } = useNavigationMenus();
  const { activeProject } = useModuleProjectContext();
  const reduxDashboardId = useSelector(selectId);

  // Memoize the navigation URL to prevent unnecessary recalculations
  const navigationUrl = useMemo(() => {
    return tabMenuItems[0]?.route
      ? sanatizeNavigationUrl({
          url: tabMenuItems[0].route,
          urlProjectId: projectId ?? '',
          activeProjectId: activeProject?.moduleProjectID ?? '',
        })
      : '';
  }, [tabMenuItems, projectId, activeProject?.moduleProjectID]);

  const analyzePages = useMemo(
    () =>
      activeProject?.pages.filter(
        (page) => page?.Route !== ANALYZE_ROUTES.root && page.Route?.startsWith(ANALYZE_ROUTES.root)
      ) ?? [],
    [activeProject?.pages]
  );

  // Remove editor redux state when not on reports page as it causes issues with the universal filters
  useEffect(() => {
    if (page !== 'reports' && reduxDashboardId) {
      dispatch(resetEditor());
    }
  }, [dispatch, page, reduxDashboardId]);

  const renderPage = () => {
    let route = APP_ROOT;
    if (module) route += `${module}`;
    route += `/${PROJECT_ID_PLACEHOLDER}`;
    if (page) route += `/${page}`;
    if (subPage) route += `/${subPage}`;
    if (tab) route += `/${tab}`;

    switch (true) {
      case route === INSIGHTS_MODULE_ROUTES.analyze:
        return (
          <Navigate
            to={sanatizeNavigationUrl({
              url: analyzePages[0]?.Route ?? '',
              urlProjectId: projectId ?? '',
              activeProjectId: activeProject?.moduleProjectID ?? '',
            })}
            replace={true}
          />
        );
      case route === INSIGHTS_MODULE_ROUTES.my_impact:
      case route === COACH_ROUTES.root:
      case route === ANALYZE_ROUTES.insights:
      case route === ANALYZE_ROUTES.alignment:
        return navigationUrl ? <Navigate to={navigationUrl} replace={true} /> : null;
      case route === DEVELOPMENT_ROUTES.root:
        return <DevelopmentPage />;
      case route === DEVELOPMENT_ROUTES.componentLibrary:
        return <ComponentLibrary />;
      case route === DEVELOPMENT_ROUTES.testingInsightsDetail:
        return <InsightsDetailPage />;
      case route === DEVELOPMENT_ROUTES.surveyConfigurator:
        return <SurveyConfiguratorPage />;
      case route === ALIGNMENT_ROUTES.summary:
        return <AlignmentSummaryPage />;
      case route === ALIGNMENT_ROUTES.detail:
        return <AlignmentDetailPage />;
      case route === ALIGNMENT_ROUTES.compare:
        return <AlignmentComparePage />;
      case route === INSIGHTS_ROUTES.summary:
        return <InsightsSummaryPage />;
      case route === INSIGHTS_ROUTES.detail:
        return <InsightsDetailPage />;
      case route === INSIGHTS_ROUTES.compare:
        return <InsightsComparePage />;
      case route === MY_IMPACT_MODULE_ROUTES.interaction_kpis:
        return <MyImpactInteractionKpisPage />;
      case route === MY_IMPACT_MODULE_ROUTES.collected_responses:
        return <MyImpactCollectedResponsesPage />;
      case route === `${ANALYZE_ROUTES.interactions}`:
        return <InteractionPage />;
      case route === `${ANALYZE_ROUTES.themeAnalytics}`:
        return <ThemeAnalyticsPage />;
      case route === `${ANALYZE_ROUTES.elsieai}`:
        return (
          <SelectionContextProvider>
            <ElsieAiPage />
          </SelectionContextProvider>
        );
      case route === `${ANALYZE_ROUTES.elsieaiV2}`:
        return (
          <SelectionContextProvider>
            <ElsieAiPageV2 />
          </SelectionContextProvider>
        );
      case route === `${ANALYZE_ROUTES.mir}`:
        return <MirPage />;
      case route === `${INSIGHTS_MODULE_ROUTES.mir}`:
        return <MirPage />;
      case route === COACH_ROUTES.summary:
        return <CoachSummaryPage />;
      case route === COACH_ROUTES.detail:
        return <CoachDetailPage />;
      case route === COACH_ROUTES.team_detail_interaction_kpis:
        return <CoachTeamDetailInteractionKpisPage />;
      case route === COACH_ROUTES.team_detail_collected_responses:
        return <CoachTeamDetailCollectedResponsesPage />;
      case route === PROFILE_CAPTURE_ROUTES.root:
        return <ProfileCapturePage />;
      case route === PROFILE_CAPTURE_ROUTES.newActivity:
        return <CaptureNewActivityPage />;
      case route === PROFILE_CAPTURE_ROUTES.newInsight:
        return <CaptureNewInsightPage />;
      case route === CAPTURE_ROUTES.root:
        return <CapturePage />;
      case route === CAPTURE_ROUTES.newActivity:
        return <CaptureNewActivityPage />;
      case route === CAPTURE_ROUTES.newInsight:
        return <CaptureNewInsightPage />;
      case route === `${REPORTS_ROUTES.root}`:
        return (
          <Navigate
            to={sanatizeNavigationUrl({
              url: REPORTS_ROUTES.library,
              urlProjectId: projectId ?? '',
              activeProjectId: activeProject?.moduleProjectID ?? '',
            })}
            replace={true}
          />
        );
      case route === REPORTS_ROUTES.library:
        return <DashboardLibraryPage />;
      case route === REPORTS_ROUTES.wizard:
        return <DashboardWizardPage />;
      case route === REPORTS_ROUTES.editor:
        return <DashboardEditorPage />;
      case route.startsWith(REPORTS_ROUTES.view):
        return <DashboardViewPage />;
      case route === REPORTS_ROUTES.catalog:
        return <WidgetCatalogPage />;
      case route === REPORTS_ROUTES.configurator:
        return <WidgetConfiguratorPage />;

      default:
        console.error(`Error: Route not found: ${route}`);
        return <ErrorPage errorType={ErrorType.NOT_FOUND} />;
    }
  };

  // Wrap only capture routes in CaptureContextProvider
  if (compareUrl([CAPTURE_ROUTES.root, PROFILE_CAPTURE_ROUTES.root], { startsWith: true })) {
    return (
      <FullLayout>
        <Suspense fallback={<Spinner />}>
          <CaptureProvider>{renderPage()}</CaptureProvider>
        </Suspense>
      </FullLayout>
    );
  }

  return (
    <FullLayout>
      <Suspense fallback={<Spinner />}>{renderPage()}</Suspense>
    </FullLayout>
  );
}

export default DynamicModule;
